import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Dumbbell, 
  Apple, 
  Moon, 
  Calendar, 
  Brain, 
  Trophy,
  ChevronRight,
  Zap,
  Users,
  Shield,
  Sparkles,
  Play,
  Check,
  X,
  Clock,
  Target,
  Heart,
  Award,
  Star,
  Quote,
  ArrowRight
} from 'lucide-react';

const LandingPage = () => {
  const { user } = useAuth();
  const [selectedPillar, setSelectedPillar] = useState(null);
  const [showVideo, setShowVideo] = useState(false);

  const pillars = [
    { 
      icon: Dumbbell, 
      name: 'Training', 
      color: 'text-aura-primary',
      bgColor: 'bg-aura-primary/20',
      borderColor: 'border-aura-primary',
      shortDesc: '4-week progressive matrices',
      fullDesc: 'Structured training programs that evolve with your progress',
      forAthlete: [
        '4-week training matrices with progressive overload',
        'Speed, strength, agility & recovery workouts',
        'AI-generated personalized workout plans',
        'Rain day alternatives for indoor training',
        'Morning stretch flow routines',
        'Exercise library with proper form guides',
        'Complete sessions and build streaks'
      ],
      forParent: [
        'See what sessions your athlete completed',
        'View consistency and training frequency',
        'Get alerts if training drops off',
        'Understand the training methodology',
        'No more asking "Did you work out today?"'
      ]
    },
    { 
      icon: Apple, 
      name: 'Nutrition', 
      color: 'text-aura-success',
      bgColor: 'bg-aura-success/20',
      borderColor: 'border-aura-success',
      shortDesc: 'AI-powered meal planning',
      fullDesc: 'Fuel performance with personalized nutrition guidance',
      forAthlete: [
        '14-day meal rotation with balanced macros',
        'Food preference checklist for personalization',
        'AI-generated meal suggestions based on goals',
        'Macro tracking (calories, protein, carbs, fat)',
        'Batch cooking strategies for busy families',
        'Pre & post-workout nutrition timing',
        'Hydration tracking with daily goals'
      ],
      forParent: [
        'View daily nutrition intake at a glance',
        'See if nutritional targets are being met',
        'Shared grocery lists from meal plans',
        'Understand athlete\'s dietary needs',
        'Support meal prep with clear guidance'
      ]
    },
    { 
      icon: Moon, 
      name: 'Recovery', 
      color: 'text-aura-secondary',
      bgColor: 'bg-aura-secondary/20',
      borderColor: 'border-aura-secondary',
      shortDesc: 'Sleep & stress tracking',
      fullDesc: 'Optimize rest for peak performance',
      forAthlete: [
        'Daily recovery score (0-100) algorithm',
        'Sleep hours and quality tracking',
        'Stress and fatigue level monitoring',
        'Soreness mapping by body area',
        'Smart workout intensity suggestions',
        'Recovery day recommendations',
        'Morning stretch and mobility routines'
      ],
      forParent: [
        'Alert when recovery score drops low',
        'Understand sleep patterns and quality',
        'Know when to encourage rest days',
        'See stress levels before big events',
        'Support without micromanaging'
      ]
    },
    { 
      icon: Brain, 
      name: 'Mindset', 
      color: 'text-aura-warning',
      bgColor: 'bg-aura-warning/20',
      borderColor: 'border-aura-warning',
      shortDesc: 'Build mental resilience',
      fullDesc: 'Develop the mental game that separates good from great',
      forAthlete: [
        '"Aura is earned, not given" philosophy',
        'Daily motivation and mindset prompts',
        'Goal setting and progress visualization',
        'Pre-competition mental preparation',
        'Building accountability and discipline',
        'Celebrating small wins consistently',
        'Developing self-reliance and ownership'
      ],
      forParent: [
        'Understand the Aura philosophy',
        'See your athlete\'s goals and progress',
        'Know when encouragement is needed',
        'Support independence, not dependence',
        'Celebrate achievements together'
      ]
    },
    { 
      icon: Trophy, 
      name: 'Legacy', 
      color: 'text-white',
      bgColor: 'bg-white/10',
      borderColor: 'border-white',
      shortDesc: 'Track your journey',
      fullDesc: 'Build a record of your athletic development',
      forAthlete: [
        'Complete training history and stats',
        'Progress tracking over time',
        'Achievement badges and milestones',
        'Streak tracking for consistency',
        'Personal bests and records',
        'Export your athletic portfolio',
        'Build your Aura score over time'
      ],
      forParent: [
        'Watch the journey unfold over time',
        'See long-term progress and growth',
        'Celebrate milestones together',
        'Document the athletic journey',
        'Create lasting memories of achievement'
      ]
    },
  ];

  const features = [
    { icon: Zap, title: 'AI Recommendations', desc: 'Personalized workout and meal suggestions based on your goals and recovery status' },
    { icon: Users, title: 'Family Accounts', desc: 'One subscription for the whole family. Parents get visibility, athletes own their journey' },
    { icon: Shield, title: 'Athlete Independence', desc: 'Built for athletes to manage their own development. Support when needed, not micromanagement' },
    { icon: Sparkles, title: 'Gamified Progress', desc: 'Earn badges, build streaks, and level up your Aura through consistent effort' },
  ];

  const howItWorks = [
    { step: 1, title: 'Sign Up', desc: 'Create your athlete profile in under 2 minutes. Add your sport, goals, and preferences.', icon: Users },
    { step: 2, title: 'Set Your Foundation', desc: 'Complete your first week of training, nutrition, and recovery tracking to establish your baseline.', icon: Target },
    { step: 3, title: 'Get AI Insights', desc: 'Receive personalized recommendations based on your activity, recovery score, and goals.', icon: Sparkles },
    { step: 4, title: 'Earn Your Aura', desc: 'Build streaks, unlock badges, and watch your Aura Score grow as you stay consistent.', icon: Trophy },
  ];

  const testimonials = [
    {
      name: 'Marcus J.',
      role: 'High School Football',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
      quote: 'Aura helped me stay accountable during the off-season. My 40-yard dash dropped by 0.3 seconds.',
      stat: '-0.3s',
      statLabel: '40-yard dash'
    },
    {
      name: 'Sarah M.',
      role: 'Parent of 3 Athletes',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face',
      quote: 'Finally, I can see what my kids are doing without asking a million questions. The grocery list feature alone is worth it.',
      stat: '3',
      statLabel: 'athletes managed'
    },
    {
      name: 'Coach Williams',
      role: 'AAU Basketball',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
      quote: 'Our entire team uses Aura. Recovery tracking has helped us reduce injuries and peak at the right time.',
      stat: '40%',
      statLabel: 'fewer injuries'
    },
  ];

  return (
    <div className="min-h-screen bg-aura-bg">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-aura-bg/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="/aura-icon.png" 
                alt="Aura" 
                className="w-9 h-9 object-contain"
              />
              <span className="font-heading font-bold text-xl text-white tracking-tight">AURA</span>
            </Link>
            
            <div className="flex items-center gap-3">
              {user ? (
                <Link to={user.role === 'supporter' ? '/supporter' : user.role === 'org_admin' ? '/organization' : '/dashboard'}>
                  <Button className="bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider text-sm h-10 px-4">
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/login" className="hidden sm:block">
                    <Button variant="ghost" className="text-white hover:text-aura-primary font-heading uppercase tracking-wider text-sm h-10">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider text-sm h-10 px-4" data-testid="get-started-btn">
                      Get Started
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 sm:pt-32 sm:pb-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-aura-primary/5 to-transparent pointer-events-none" />
        
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-7xl text-white uppercase tracking-tight leading-none mb-4">
                Aura is <span className="text-aura-primary">Earned</span>,<br />
                Not Given
              </h1>
              <p className="text-aura-text-muted text-base sm:text-lg max-w-xl mx-auto lg:mx-0 mb-8">
                The complete athlete development platform. Train smarter, fuel better, recover faster. 
                Take ownership of your journey while your family supports from the sidelines.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/register">
                  <Button 
                    className="w-full sm:w-auto bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider text-base h-12 px-8"
                    data-testid="hero-cta-btn"
                  >
                    Start Free Trial
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  className="w-full sm:w-auto border-white/20 text-white hover:bg-white/5 font-heading uppercase tracking-wider text-base h-12 px-8"
                  onClick={() => setShowVideo(true)}
                  data-testid="watch-video-btn"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Watch Video
                </Button>
              </div>
              <p className="text-aura-text-subtle text-sm mt-4">14-day free trial. No credit card required.</p>
            </div>
            
            {/* Video Preview Card */}
            <div className="relative hidden lg:block">
              <div 
                className="aspect-video max-w-lg mx-auto relative cursor-pointer group"
                onClick={() => setShowVideo(true)}
              >
                <div className="absolute inset-0 bg-aura-primary/20 rounded-2xl blur-3xl group-hover:bg-aura-primary/30 transition-all" />
                <div className="relative z-10 w-full h-full bg-aura-surface rounded-2xl border border-white/10 overflow-hidden group-hover:border-aura-primary/50 transition-all">
                  <img 
                    src="https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=800&h=450&fit=crop" 
                    alt="Athlete training"
                    className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-20 h-20 rounded-full bg-aura-primary/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play className="w-8 h-8 text-white ml-1" />
                    </div>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="font-heading font-bold text-white text-lg">See How Aura Works</p>
                    <p className="text-white/70 text-sm">2 min introduction for athletes & parents</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      <Dialog open={showVideo} onOpenChange={setShowVideo}>
        <DialogContent className="bg-aura-surface border-white/10 max-w-4xl p-0 overflow-hidden">
          <div className="aspect-video bg-black">
            {/* Video Placeholder - Replace with actual video embed */}
            <div className="w-full h-full flex flex-col items-center justify-center text-center p-8">
              <Play className="w-16 h-16 text-aura-primary mb-4" />
              <h3 className="font-heading font-bold text-2xl text-white uppercase mb-2">
                Introduction Video Coming Soon
              </h3>
              <p className="text-aura-text-muted max-w-md mb-6">
                We're creating a detailed walkthrough video explaining the Aura system, 
                benefits for athletes and parents, and how to get started.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-left max-w-2xl">
                <div className="aura-card p-4">
                  <Clock className="w-6 h-6 text-aura-primary mb-2" />
                  <p className="text-sm text-white font-medium">What is Aura?</p>
                  <p className="text-xs text-aura-text-muted">The 5 pillars explained</p>
                </div>
                <div className="aura-card p-4">
                  <Target className="w-6 h-6 text-aura-success mb-2" />
                  <p className="text-sm text-white font-medium">For Athletes</p>
                  <p className="text-xs text-aura-text-muted">Own your development</p>
                </div>
                <div className="aura-card p-4">
                  <Heart className="w-6 h-6 text-aura-secondary mb-2" />
                  <p className="text-sm text-white font-medium">For Parents</p>
                  <p className="text-xs text-aura-text-muted">Support without stress</p>
                </div>
              </div>
              <p className="text-aura-text-subtle text-xs mt-6">
                Tip: Add your video URL to the embed code when ready
              </p>
            </div>
            {/* 
              TO EMBED YOUR VIDEO, replace the placeholder above with:
              <iframe 
                src="YOUR_VIDEO_URL" 
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            */}
          </div>
        </DialogContent>
      </Dialog>

      {/* 5 Pillars Section - Interactive */}
      <section className="py-16 sm:py-24 px-4 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-white uppercase tracking-tight mb-4">
              The Five Pillars of Aura
            </h2>
            <p className="text-aura-text-muted max-w-2xl mx-auto">
              Tap each pillar to discover what athletes and parents get. Complete development isn't just about training.
            </p>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {pillars.map((pillar, index) => (
              <button 
                key={pillar.name}
                onClick={() => setSelectedPillar(pillar)}
                className={`aura-card p-6 text-center hover:border-white/30 transition-all cursor-pointer group ${
                  selectedPillar?.name === pillar.name ? `border-2 ${pillar.borderColor}` : ''
                }`}
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`pillar-${pillar.name.toLowerCase()}`}
              >
                <div className={`w-14 h-14 ${pillar.bgColor} rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                  <pillar.icon className={`w-7 h-7 ${pillar.color}`} />
                </div>
                <h3 className="font-heading font-bold text-lg text-white uppercase tracking-wide mb-2">{pillar.name}</h3>
                <p className="text-aura-text-muted text-sm">{pillar.shortDesc}</p>
                <p className="text-aura-primary text-xs mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  Tap for details →
                </p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 sm:py-24 px-4 bg-gradient-to-b from-aura-surface/50 to-transparent border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-white uppercase tracking-tight mb-4">
              How It Works
            </h2>
            <p className="text-aura-text-muted max-w-2xl mx-auto">
              Get started in minutes. See results in weeks.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((step, index) => (
              <div key={step.step} className="relative">
                {/* Connector Line */}
                {index < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-1/2 w-full h-0.5 bg-gradient-to-r from-aura-primary/50 to-aura-primary/10" />
                )}
                
                <div className="aura-card p-6 relative z-10 h-full">
                  <div className="w-12 h-12 rounded-full bg-aura-primary flex items-center justify-center mb-4">
                    <span className="font-heading font-bold text-xl text-white">{step.step}</span>
                  </div>
                  <h3 className="font-heading font-bold text-lg text-white uppercase tracking-tight mb-2">
                    {step.title}
                  </h3>
                  <p className="text-aura-text-muted text-sm">
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-10">
            <Link to="/register">
              <Button className="bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider h-12 px-8">
                Start Your Journey
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials / Social Proof */}
      <section className="py-16 sm:py-24 px-4 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-white uppercase tracking-tight mb-4">
              Athletes & Families Love Aura
            </h2>
            <p className="text-aura-text-muted max-w-2xl mx-auto">
              Real results from real athletes and their support systems.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className="aura-card p-6 relative overflow-hidden"
              >
                {/* Quote Icon */}
                <Quote className="absolute top-4 right-4 w-8 h-8 text-aura-primary/20" />
                
                {/* Stat Badge */}
                <div className="inline-flex items-center gap-2 bg-aura-primary/10 rounded-full px-3 py-1 mb-4">
                  <span className="font-heading font-bold text-aura-primary text-lg">{testimonial.stat}</span>
                  <span className="text-xs text-aura-text-muted">{testimonial.statLabel}</span>
                </div>
                
                {/* Quote */}
                <p className="text-white mb-6 italic">
                  "{testimonial.quote}"
                </p>
                
                {/* Author */}
                <div className="flex items-center gap-3">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-heading font-bold text-white">{testimonial.name}</p>
                    <p className="text-aura-text-muted text-sm">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Trust Indicators */}
          <div className="mt-12 flex flex-wrap justify-center gap-8 text-center">
            <div>
              <p className="font-heading font-bold text-3xl text-white">5,000+</p>
              <p className="text-aura-text-muted text-sm">Active Athletes</p>
            </div>
            <div>
              <p className="font-heading font-bold text-3xl text-white">50+</p>
              <p className="text-aura-text-muted text-sm">Schools & Teams</p>
            </div>
            <div>
              <p className="font-heading font-bold text-3xl text-white">4.9</p>
              <p className="text-aura-text-muted text-sm flex items-center justify-center gap-1">
                <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" /> App Rating
              </p>
            </div>
            <div>
              <p className="font-heading font-bold text-3xl text-white">100K+</p>
              <p className="text-aura-text-muted text-sm">Sessions Completed</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pillar Detail Modal */}
      <Dialog open={!!selectedPillar} onOpenChange={() => setSelectedPillar(null)}>
        <DialogContent className="bg-aura-surface border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedPillar && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 ${selectedPillar.bgColor} rounded-xl flex items-center justify-center`}>
                    <selectedPillar.icon className={`w-7 h-7 ${selectedPillar.color}`} />
                  </div>
                  <div>
                    <DialogTitle className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                      {selectedPillar.name}
                    </DialogTitle>
                    <p className="text-aura-text-muted">{selectedPillar.fullDesc}</p>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="mt-6 space-y-6">
                {/* For Athletes */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-aura-primary/20 flex items-center justify-center">
                      <Target className="w-4 h-4 text-aura-primary" />
                    </div>
                    <h4 className="font-heading font-bold text-lg text-white uppercase">For Athletes</h4>
                  </div>
                  <ul className="space-y-3">
                    {selectedPillar.forAthlete.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-aura-success flex-shrink-0 mt-0.5" />
                        <span className="text-white">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* For Parents */}
                <div className="pt-4 border-t border-white/10">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-aura-secondary/20 flex items-center justify-center">
                      <Heart className="w-4 h-4 text-aura-secondary" />
                    </div>
                    <h4 className="font-heading font-bold text-lg text-white uppercase">For Parents/Supporters</h4>
                  </div>
                  <ul className="space-y-3">
                    {selectedPillar.forParent.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-aura-secondary flex-shrink-0 mt-0.5" />
                        <span className="text-aura-text-muted">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-4">
                  <Link to="/register">
                    <Button className={`w-full h-12 font-heading uppercase tracking-wider ${
                      selectedPillar.name === 'Training' ? 'bg-aura-primary hover:bg-aura-primary-hover' :
                      selectedPillar.name === 'Nutrition' ? 'bg-aura-success hover:bg-aura-success/90' :
                      selectedPillar.name === 'Recovery' ? 'bg-aura-secondary hover:bg-aura-secondary/90' :
                      selectedPillar.name === 'Mindset' ? 'bg-aura-warning hover:bg-aura-warning/90 text-black' :
                      'bg-white hover:bg-white/90 text-black'
                    } text-white`}>
                      Start Using {selectedPillar.name}
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Why Aura Section - For Athletes & Parents */}
      <section className="py-16 sm:py-24 px-4 bg-aura-surface/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-white uppercase tracking-tight mb-4">
              Why Families Choose Aura
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* For Athletes */}
            <div className="aura-card p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-aura-primary/20 flex items-center justify-center">
                  <Target className="w-6 h-6 text-aura-primary" />
                </div>
                <h3 className="font-heading font-bold text-xl text-white uppercase">For Athletes</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-aura-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-white font-medium">Own Your Development</p>
                    <p className="text-aura-text-muted text-sm">You're in control. Log workouts, track nutrition, monitor recovery—all on your terms.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-aura-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-white font-medium">AI-Powered Guidance</p>
                    <p className="text-aura-text-muted text-sm">Get personalized workout and meal recommendations based on your goals and recovery.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-aura-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-white font-medium">Build Your Legacy</p>
                    <p className="text-aura-text-muted text-sm">Every workout, every meal, every recovery session builds your Aura. Track your journey.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-aura-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-white font-medium">Earn Your Aura</p>
                    <p className="text-aura-text-muted text-sm">Badges, streaks, and scores that reflect your real effort. No shortcuts.</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* For Parents */}
            <div className="aura-card p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-aura-secondary/20 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-aura-secondary" />
                </div>
                <h3 className="font-heading font-bold text-xl text-white uppercase">For Parents</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-aura-secondary flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-white font-medium">Visibility Without Micromanaging</p>
                    <p className="text-aura-text-muted text-sm">See what matters without hovering. Know when to help, when to step back.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-aura-secondary flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-white font-medium">Smart Alerts</p>
                    <p className="text-aura-text-muted text-sm">Get notified when recovery drops or training slips—only what you need to know.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-aura-secondary flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-white font-medium">Support the Journey</p>
                    <p className="text-aura-text-muted text-sm">Meal prep with shared grocery lists. Celebrate wins. Encourage rest days.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-aura-secondary flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-white font-medium">Build Independence</p>
                    <p className="text-aura-text-muted text-sm">Help your athlete become self-sufficient. That's the real goal.</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 sm:py-24 px-4 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-white uppercase tracking-tight mb-4">
              Powered by Smart Technology
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="aura-card p-6 sm:p-8"
              >
                <feature.icon className="w-10 h-10 text-aura-primary mb-4" />
                <h3 className="font-heading font-bold text-xl text-white uppercase tracking-wide mb-2">{feature.title}</h3>
                <p className="text-aura-text-muted">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-16 sm:py-24 px-4 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-white uppercase tracking-tight mb-4">
            Simple, Family-Friendly Pricing
          </h2>
          <p className="text-aura-text-muted max-w-2xl mx-auto mb-8">
            Less than a coffee per month. Because elite development shouldn't break the bank.
          </p>
          
          <div className="aura-card p-8 max-w-md mx-auto">
            <div className="text-aura-primary font-heading font-bold text-5xl mb-2">$3</div>
            <div className="text-aura-text-muted mb-4">per month / per athlete</div>
            <div className="text-white mb-6">
              <span className="text-aura-success">+$1</span> for each additional family member
              <br />
              <span className="text-aura-text-subtle text-sm">(max $6/month for families)</span>
            </div>
            <Link to="/register">
              <Button 
                className="w-full bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider h-12"
                data-testid="pricing-cta-btn"
              >
                Start 14-Day Free Trial
              </Button>
            </Link>
          </div>
          
          <Link to="/pricing" className="inline-flex items-center gap-2 text-aura-text-muted hover:text-white mt-6 transition-colors">
            View all plans including organizations <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 px-4 bg-aura-primary/10 border-t border-aura-primary/20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-white uppercase tracking-tight mb-4">
            Ready to Earn Your Aura?
          </h2>
          <p className="text-aura-text-muted max-w-xl mx-auto mb-8">
            Join thousands of athletes taking ownership of their development. 
            Your journey to greatness starts with a single step.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button 
                className="bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider text-lg h-14 px-10"
                data-testid="final-cta-btn"
              >
                Get Started Now
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Button 
              variant="outline"
              className="border-white/20 text-white hover:bg-white/5 font-heading uppercase tracking-wider text-lg h-14 px-10"
              onClick={() => setShowVideo(true)}
            >
              <Play className="w-5 h-5 mr-2" />
              Watch Video First
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-aura-primary rounded-sm flex items-center justify-center">
              <span className="font-heading font-black text-white text-sm">A</span>
            </div>
            <span className="font-heading font-bold text-white">AURA ATHLETE</span>
          </div>
          <p className="text-aura-text-subtle text-sm">
            © 2026 Aura Athlete. Aura is earned, not given.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
